import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTenant } from "../../context/TenantContext";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const PurchaseAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { shop: currentShop } = useTenant();
  const { handleAdd, loading, resetForm } = useAdd("shop/purchases", token);
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier"],
    "shop/suppliers?page_size=all", 
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const suppliers = suppliersData?.results || suppliersData || [];
  const products = productsData?.results || productsData || [];
  const units = unitsData?.results || unitsData || [];
  const currencies = getUniqueCurrencies(currenciesData?.results || currenciesData || []);
  const shopId = currentShop?.id ? String(currentShop.id) : "";
  const [supplier, setSupplier] = useState("");
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-add", shopId],
    shopId ? `shop/accounts/?page_size=all&shop=${shopId}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const filteredSuppliers = suppliers;
  const selectedSupplierOption =
    filteredSuppliers.find((sup) => String(sup.id) === String(supplier)) || null;
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(purchaseCurrency)),
    [accounts, purchaseCurrency]
  );

  const { data: directRateData } = useFetchObjects(
    ["purchase-add-rate-direct", shopId, purchaseCurrency, selectedAccount?.currency],
    shopId && purchaseCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shopId}&from_currency=${purchaseCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["purchase-add-rate-reverse", shopId, purchaseCurrency, selectedAccount?.currency],
    shopId && purchaseCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shopId}&from_currency=${selectedAccount.currency}&to_currency=${purchaseCurrency}`
      : "",
    token
  );

  useEffect(() => {
    if (!shopId || !purchaseCurrency || accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [shopId, purchaseCurrency, accounts, sameCurrencyAccounts, selectedAccountId]);

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        quantity: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const getProductOptionLabel = (product) => {
    if (!product) return "";
    return product.name || "";
  };

  const getSupplierOptionLabel = (sup) => {
    if (!sup) return "";
    const name = sup.name || "";
    const contact = sup.contact ? ` • ${sup.contact}` : "";
    return `${name}${contact}`;
  };

  const getItemTotal = (item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.buy_price) || 0;
    return qty * price;
  };

  const totalAmount = items.reduce((acc, item) => {
    return acc + getItemTotal(item);
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;

  const itemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product &&
        item.unit &&
        item.quantity &&
        item.buy_price &&
        item.retail_price
    );
  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;
  const needsPaymentAccount = parsedReceived > 0;

  const exchangeRatePreview = useMemo(() => {
    if (!purchaseCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? purchaseCurrency;
    if (String(purchaseCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [purchaseCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(receivedAmount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [receivedAmount, exchangeRate]);

  const isSubmitDisabled = loading;

  const handleSubmit = () => {
    if (!purchaseCurrency) {
      setFormError(t("purchase.currencyRequired") || "Currency is required");
      return;
    }
    if (items.length === 0) {
      setFormError(
        t("purchase.atLeastOneItemRequired") ||
          "At least one purchase item is required"
      );
      return;
    }
    if (!itemsValid) {
      setFormError(
        t("purchase.completeItemFields") ||
          "Please complete all required fields for each purchase item"
      );
      return;
    }
    if (!receivedValid) {
      setFormError(
        t("purchase.receivedExceedsTotal") ||
          "Received amount should not exceed total amount"
      );
      return;
    }
    if (needsPaymentAccount && (!selectedAccountId || !exchangeRate)) {
      setFormError(
        t("purchase.accountRequired") ||
          "Select an account and exchange rate for the received amount"
      );
      return;
    }
    setFormError("");
    const payload = {
      shop: shopId,
      supplier: supplier || null,
      amount: discountValue,
      received_amount: receivedAmount,
      currency: purchaseCurrency,
      purchase_date: purchaseDate || new Date().toISOString(),
      account: needsPaymentAccount ? selectedAccountId : null,
      exchange_rate: needsPaymentAccount ? exchangeRate : "",
      payment_date: purchaseDate
        ? `${purchaseDate}T00:00:00`
        : new Date().toISOString(),
      payment_notes: paymentNotes,
      items,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setSupplier("");
      setDiscount("");
      setReceivedAmount("");
      setPurchaseCurrency("");
      setPurchaseDate("");
      setSelectedAccountId("");
      setExchangeRate("");
      setPaymentNotes("");
      setItems([]);
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("purchase.addPurchase")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Autocomplete
            options={filteredSuppliers}
            value={selectedSupplierOption}
            onChange={(_, value) => setSupplier(value ? String(value.id) : "")}
            getOptionLabel={getSupplierOptionLabel}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            loading={supplierLoading}
            disabled={supplierLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("purchase.selectSupplier")}
                fullWidth
                placeholder={t("purchase.searchSupplier", "Search supplier")}
              />
            )}
          />
          <TextField
            select
            label={t("purchase.selectCurrency")}
            fullWidth
            required
            value={purchaseCurrency}
            onChange={(e) => setPurchaseCurrency(e.target.value)}
            disabled={currencyLoading}
          >
            {(currencies || []).map((curr) => (
              <MenuItem key={curr.id} value={String(curr.id)}>
                {curr.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("purchase.purchaseDate")}
            type="date"
            fullWidth
            required
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("Discount-Amount")}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("purchase.purchaseItems")}</Typography>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Autocomplete
                  options={products || []}
                  value={
                    (products || []).find(
                      (prod) => String(prod.id) === String(item.product)
                    ) || null
                  }
                  onChange={(_, value) =>
                    handleItemChange(
                      index,
                      "product",
                      value ? String(value.id) : ""
                    )
                  }
                  getOptionLabel={getProductOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  loading={productLoading}
                  disabled={productLoading}
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("purchase.selectProduct")}
                      required
                    />
                  )}
                />
                <TextField
                  select
                  label={t("purchase.selectUnit")}
                  required
                  value={item.unit}
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                  disabled={unitLoading}
                >
                  {(units || []).map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("purchase.quantity")}
                  type="number"
                  required
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.buyPrice")}
                  type="number"
                  required
                  value={item.buy_price}
                  onChange={(e) =>
                    handleItemChange(index, "buy_price", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.salePrice", "Sale Price")}
                  type="number"
                  required
                  value={item.retail_price}
                  onChange={(e) =>
                    handleItemChange(index, "retail_price", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.total", "Total")}
                  type="number"
                  value={getItemTotal(item).toFixed(2)}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("purchase.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("purchase.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("purchase.discountAmount", "Discount Amount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("purchase.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchase.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchase.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseAdd;

